import styled, { css, keyframes } from "styled-components";
import { ReactComponent as LogoIcon } from "shared/asset/logo_hor.svg"

import { BASIC } from "shared/model/useThemeCookie"

const blinkAnimation = keyframes`
    0%, 50% {
        opacity: 1;
    }
    51%, 100% {
        opacity: 0;
    }
`;

const styles = {
    "LogoCenter": styled.div`
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: -1;

        & > img {
            width: 700px !important;
            height: 700px !important;
            object-fit: contain;
            z-index: -2;
            opacity: ${({ $logoTransparency }) => {
                if (typeof $logoTransparency === 'number') {
                    return $logoTransparency / 100;
                }
                return 1;
            }};

            @media (max-width: 1280px) {
                width: 600px !important;
                height: 600px !important;
            }
        }
    `,
    "LogoIcon": styled(LogoIcon)`
        fill: ${({ $themeType, theme }) => $themeType === BASIC ? theme.color?.basic?.font : theme.color?.gradation?.font};

        @media (max-width: 1280px) {
            height: 42px;
            width: 238px;
        }
    `,
    "ContentDiv": styled.div`
        flex-grow: 1;
        display: flex;
        position: relative;
        height: 100%;
    `,
    "TimerDiv": styled.div`
        flex-grow: 1;
        display: flex;
        justify-content: center;
        align-items: center;
        border-right: 2px solid ${({ $themeType, theme }) => $themeType === BASIC ? theme.color?.basic?.font : theme.color?.gradation?.font};
       position: relative;

         &::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: ${({ $themeType, theme }) => $themeType === BASIC ? theme.color?.basic?.bg : theme.color?.gradation?.bg};
            opacity: 0.5;
            z-index: -1;
        }
        @media (max-width: 1280px) {
            border-right: none;
        }
    `,
    "TimerInfo": styled.div`
        position: relative;
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 0 40px;

        & > h1 {
            font-family: bold;
            line-height: 1;
            font-size: ${({ $timerStyles }) => $timerStyles?.full?.h1.fontSize || '300px'};
            margin: ${({ $timerStyles }) => $timerStyles?.full?.h1.margin || '40px'};

            ${({ $warning }) => $warning && css`
                color: red;
                animation: ${blinkAnimation} 1s infinite;
            `}
        }

        & > h2 {
            font-family: bold;
            font-size: ${({ $timerStyles }) => $timerStyles?.full?.h2.fontSize || '54px'};
            text-align: center;
        }

        & > h3 {
            font-size: ${({ $timerStyles }) => $timerStyles?.full?.h3.fontSize || '38px'};
            text-align: center;
        }

        @media (max-width: 1280px) {
            padding: 0 20px;

            & > h1 {
                font-family: 'Pretendard';
                font-weight: 700;
                line-height: 0.7;
                line-height: 1;
                font-size: ${({ $timerStyles }) => $timerStyles?.split?.h1?.fontSize || '264px'};
                margin: ${({ $timerStyles }) => $timerStyles?.split?.h1?.margin || '30px 0'};
                width: 780px;
                text-align: center;
            }

            & > h2 {
                font-family: 'Pretendard';
                font-size: ${({ $timerStyles }) => $timerStyles?.split?.h2?.fontSize || '42px'};
                font-weight: 700;
            }

            & > h3 {
                font-family: 'Pretendard';
                font-size: ${({ $timerStyles }) => $timerStyles?.split?.h3?.fontSize || '24px'};
                font-weight: 600;
            }
        }
    `,
    "TimerTopDiv": styled.div`
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 120px;

        & > h1 {
            font-family: bold;
            font-size: 52px;
        }

        & > div {
            width: fit-content;
            display: flex;
            align-items: center;
            gap: 24px;

            & > h2 {
                font-family: bold;
                font-size: 40px;
                height: 40px;
                line-height: 0.8;
            }

            & > p {
                font-size: 32px;
                line-height: 1;
                padding: 12px 40px 16px 40px;
                border: 2px solid ${({ $themeType, theme }) => $themeType === BASIC ? theme.color?.basic?.font : theme.color?.gradation?.font};
                border-radius: 9999px;
            }
        }

        @media (max-width: 1280px) {
            flex-direction: column;
            gap: 16px;

            & > h1 {
                font-family: 'Pretendard';
                font-size: 40px;
                font-weight: 700;
            }

            & > div {
                gap: 16px;

                & > h2 {
                    font-family: 'Pretendard';
                    font-size: 32px;
                    height: 23px;
                    line-height: 0.8;
                    font-weight: 600;
                }

                & > p {
                    font-family: 'Pretendard';
                    font-size: 24px;
                    line-height: 1;
                    padding: 12px 30px;
                    font-weight: 600;
                    width: 170px;
                    text-align: center;
                }
            }
        }
    `,
    "TimerBottomDiv": styled.div`
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 80px;
        margin: 35px 0 35px 0;

        & > div {
            width: fit-content;
            display: flex;
            align-items: center;
            gap: 24px;

            & > h2 {
                font-family: bold;
                font-size: 40px;
                height: 40px;
                line-height: 0.8;
            }

            & > p {
                font-size: 32px;
                line-height: 1;
                padding: 12px 40px 16px 40px;
                border: 2px solid ${({ $themeType, theme }) => $themeType === BASIC ? theme.color?.basic?.font : theme.color?.gradation?.font};
                border-radius: 9999px;
            }
        }

        @media (max-width: 1280px) {
            gap: 64px;
            margin: 32px 0;

            & > div {
                gap: 16px;

                & > h2 {
                    font-family: 'Pretendard';
                    font-size: 32px;
                    height: 25px;
                    line-height: 0.7;
                    font-weight: 600;
                }

                & > p {
                    font-family: 'Pretendard';
                    font-size: 24px;
                    padding: 12px 30px;
                    font-weight: 600;
                    width: 170px;
                    text-align: center;
                }
            }
        }
    `,

    "PrizeDiv": styled.div`
        height: 100%;
        min-height: 0;
        width: 320px;
        min-width: 320px;
        display: flex;
        flex-direction: column;
        gap: 24px;
        padding: 24px 24px 40px 24px;

        @media (max-width: 1280px) {
            display: none;
        }
    `,
    "PrizeListDiv": styled.div`
        flex-grow: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
        gap: 8px;

        & > h2 {
            width: 100%;
            text-align: center;
            font-family: bold;
            font-size: 40px;
            min-height: 40px;
            line-height: 1;
        }
    `,
    "PrizeList": styled.div`
        display: flex;
        flex-direction: column;
        gap: 4px;
        overflow-y: auto;
        -ms-overflow-style: none;
        &::-webkit-scrollbar {
            display: none;
        }

        & > div {
            display: flex;
            justify-content: space-between;
            align-items: center;

            & > p {
                font-family: bold;
                font-size: 24px;
            }
        }
    `,
    "TotlePrizeDiv": styled.div`
        height: fit-content;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;

        & > h2 {
            width: 100%;
            text-align: center;
            font-family: bold;
            font-size: 40px;
            min-height: 40px;
            line-height: 1;
        }

        & > p {
            font-size: 24px;
            line-height: 1.2;
            text-overflow: ellipsis;
            overflow: hidden;
            display: -webkit-box;
            -webkit-line-clamp: 5;
            -webkit-box-orient: vertical
        }
    `,

    "FooterDiv": styled.div`
        height: 160px;
        min-height: 160px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-left: 80px;
        padding-right: 52px;
        border-top: 2px solid ${({ $themeType, theme }) => $themeType === BASIC ? theme.color?.basic?.font : theme.color?.gradation?.font};
    `,
    "FooterInfo": styled.div`
        width: fit-content;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;

        & > h2 {
            font-family: bold;
            font-size: 32px;
            height: 32px;
            line-height: 0.8;
        }

        & > p {
            font-family: bold;
            font-size: 48px;
            height: 48px;
            line-height: 0.8;
        }
    `,
    "LogoDiv": styled.div`
        width: fit-content;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 16px;

        & > img {
            width: 100px;
            height: 100px;
            object-fit: contain;
            margin: auto;
        }
    `,
}

export default styles;